import React, { useState, useEffect } from 'react';
import { Save, X, Gift } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import type { User } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const UsersTab: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editedBalance, setEditedBalance] = useState<string>('');
  const [editingRPBalance, setEditingRPBalance] = useState<string | null>(null);
  const [editedRPBalance, setEditedRPBalance] = useState<string>('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;

      // Fetch RP balances for all users
      const { data: rewardsData, error: rewardsError } = await supabase
        .from('user_rewards')
        .select('user_id, rp_balance');

      if (rewardsError) throw rewardsError;

      // Create a map of user_id to rp_balance
      const rewardsMap = new Map(
        rewardsData?.map(reward => [reward.user_id, reward.rp_balance]) || []
      );

      // Combine users with their RP balances
      const usersWithRP = usersData?.map(user => ({
        ...user,
        rp_balance: rewardsMap.get(user.id) || 0
      })) || [];

      setUsers(usersWithRP);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBalance = async (userId: string) => {
    try {
      const newBalance = parseFloat(editedBalance);
      if (isNaN(newBalance)) {
        toast.error('Please enter a valid number');
        return;
      }

      const { error } = await supabase
        .from('users')
        .update({ balance: newBalance })
        .eq('id', userId);

      if (error) throw error;

      await supabase.from('system_logs').insert({
        user_id: user?.id,
        action: 'UPDATE_BALANCE',
        details: `Updated balance for user ${userId} to ₽${newBalance}`
      });

      toast.success('Balance updated successfully');
      setEditingUser(null);
      setEditedBalance('');
      fetchUsers();
    } catch (error) {
      console.error('Error updating balance:', error);
      toast.error('Failed to update balance');
    }
  };

  const handleUpdateRPBalance = async (userId: string) => {
    try {
      const newRPBalance = parseInt(editedRPBalance);
      if (isNaN(newRPBalance)) {
        toast.error('Please enter a valid number');
        return;
      }

      // Use upsert to handle both insert and update cases
      const { error } = await supabase
        .from('user_rewards')
        .upsert({
          user_id: userId,
          rp_balance: newRPBalance,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      await supabase.from('system_logs').insert({
        user_id: user?.id,
        action: 'UPDATE_RP_BALANCE',
        details: `Updated RP balance for user ${userId} to ${newRPBalance} RP`
      });

      // Create a reward transaction record
      const currentUser = users.find(u => u.id === userId);
      const currentRP = currentUser?.rp_balance || 0;
      const difference = newRPBalance - currentRP;

      if (difference !== 0) {
        await supabase.from('reward_transactions').insert({
          user_id: userId,
          amount: difference,
          type: difference > 0 ? 'earn' : 'spend',
          description: 'Manual RP balance adjustment by admin'
        });
      }

      toast.success('RP balance updated successfully');
      setEditingRPBalance(null);
      setEditedRPBalance('');
      fetchUsers();
    } catch (error) {
      console.error('Error updating RP balance:', error);
      toast.error('Failed to update RP balance');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">User Management</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Balance</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">RP Balance</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Created At</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {users.map((user) => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <img
                      src={user.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || user.email)}&background=random`}
                      alt="Avatar"
                      className="h-10 w-10 rounded-full object-cover"
                    />
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {user.name || 'No name set'}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{user.email}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{user.role}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {editingUser === user.id ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={editedBalance}
                        onChange={(e) => setEditedBalance(e.target.value)}
                        className="w-24 px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="New balance"
                      />
                      <button
                        onClick={() => handleUpdateBalance(user.id)}
                        className="p-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                      >
                        <Save className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingUser(null);
                          setEditedBalance('');
                        }}
                        className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <span className="text-gray-500 dark:text-gray-400">₽{user.balance.toLocaleString('ru-RU')}</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {editingRPBalance === user.id ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={editedRPBalance}
                        onChange={(e) => setEditedRPBalance(e.target.value)}
                        className="w-24 px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="New RP balance"
                      />
                      <button
                        onClick={() => handleUpdateRPBalance(user.id)}
                        className="p-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                      >
                        <Save className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingRPBalance(null);
                          setEditedRPBalance('');
                        }}
                        className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <Gift className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                      <span className="text-gray-500 dark:text-gray-400">{user.rp_balance}</span>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{new Date(user.created_at).toLocaleString()}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex space-x-2">
                    {editingUser !== user.id && (
                      <button
                        onClick={() => {
                          setEditingUser(user.id);
                          setEditedBalance(user.balance.toString());
                        }}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                      >
                        Edit Balance
                      </button>
                    )}
                    {editingRPBalance !== user.id && (
                      <button
                        onClick={() => {
                          setEditingRPBalance(user.id);
                          setEditedRPBalance(user.rp_balance.toString());
                        }}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                      >
                        Edit RP
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsersTab;